import React from 'react';
import {
  Autocomplete,
  Checkbox,
  Chip,
  FormControlLabel,
  MenuItem,
  Stack,
  Typography,
} from '@mui/material';
import { TokenAwareTextField as TextField } from './TokenAwareTextField';
import { useCampaignContentBlocks, useCampaignMutations } from '../../../../hooks/campaign-hooks';
import { CampaignContentBlock, CampaignContentBlockPayload } from '../../../../support/campaigns';
import { CategorySchema, PayloadFieldDef } from './contentBlockSchemas';
import { ReferenceMultiSelect, ReferenceOption, ReferenceSelect } from './ReferenceSelect';
import { ProseQuestionEditor, QuestionBlockEditor } from './StepQuestionsFields';
import { TokenTextField } from './TokenTextField';
import { arrToLines, linesToArr } from './builderShared';
import { useCampaignCapabilityReference } from './builderReferences';

/**
 * Generic, schema-driven editor for a content block's `payload` — the friendly replacement for the raw
 * JSON textarea. The exact analog of `Admin/TileBuilder/ConfigFieldForm`: it renders one input per
 * `PayloadFieldDef` in the category's `CategorySchema` (`switch (field.type)`), never hand-coding a
 * per-category form. Look-and-feel matches the rest of the builder (small MUI fields, commit-on-blur).
 *
 * Two field types delegate to the existing first-class question editors instead of a scalar input:
 * `bandedOptions` -> `QuestionBlockEditor` (edits `ask`/`why`/`options`) and `proseQuestion` ->
 * `ProseQuestionEditor` (edits `question`/`why`/`category`/`when`/`pillar`). Those editors already save
 * a full merged payload, so they compose with the scalar fields — every save goes through the same
 * `onSavePayload` (a full-record PUT), each merging its keys over the latest server payload.
 *
 * Scalar fields hold a local draft reseeded whenever the server payload changes; a commit merges the
 * converted draft over the current payload and skips the write when nothing changed (JSON compare), so
 * we never issue a redundant PUT. Unmapped payload keys are always preserved.
 */

const isDelegated = (t: PayloadFieldDef['type']): boolean =>
  t === 'bandedOptions' || t === 'proseQuestion';

/** Build the editable draft (raw input forms) for the scalar fields from the server payload. */
function draftFromPayload(
  fields: PayloadFieldDef[],
  payload: Record<string, unknown>,
): Record<string, unknown> {
  const draft: Record<string, unknown> = {};
  for (const f of fields) {
    if (isDelegated(f.type)) continue;
    const v = payload[f.key];
    switch (f.type) {
      case 'lines':
        draft[f.key] = arrToLines(v);
        break;
      case 'chips':
      case 'capabilityRefs':
        draft[f.key] = Array.isArray(v) ? (v as string[]) : [];
        break;
      case 'contentBlockRef':
        draft[f.key] = typeof v === 'string' ? v : v == null ? '' : String(v);
        break;
      case 'boolean':
        draft[f.key] = v === true;
        break;
      case 'number':
        draft[f.key] = typeof v === 'number' ? v : v == null || v === '' ? '' : Number(v);
        break;
      default:
        draft[f.key] = typeof v === 'string' ? v : v == null ? '' : String(v);
    }
  }
  return draft;
}

/** Convert one field's draft (raw input) value into the shape stored on the payload. */
function toPayloadValue(field: PayloadFieldDef, raw: unknown): unknown {
  switch (field.type) {
    case 'lines':
      return linesToArr(String(raw ?? ''));
    case 'chips':
    case 'capabilityRefs':
      return Array.isArray(raw) ? raw : [];
    case 'contentBlockRef':
      return raw ?? '';
    case 'boolean':
      return raw === true;
    case 'number':
      return raw === '' || raw == null ? '' : Number(raw);
    default:
      return raw ?? '';
  }
}

export function SchemaPayloadFields(props: {
  block: CampaignContentBlock;
  schema: CategorySchema;
  canWrite: boolean;
  campaignId: number;
  onSavePayload: (payload: CampaignContentBlockPayload) => void;
}): JSX.Element {
  const { block, schema, canWrite, campaignId, onSavePayload } = props;
  const payload = React.useMemo(
    () => (block.payload ?? {}) as Record<string, unknown>,
    [block.payload],
  );

  // Reference option sources: the campaign's capabilities (for `capabilityRefs`) and all its content
  // blocks (grouped by category, for `contentBlockRef`/constrained `chips`). Both DB-driven, so the
  // pickers offer exactly the authored objects rather than a hard-coded list.
  const { data: allBlocks } = useCampaignContentBlocks(campaignId);
  const blockMutations = useCampaignMutations('contentBlock');
  const { capabilityOptions, createCapability } = useCampaignCapabilityReference(campaignId);

  const blocksByCategory = React.useMemo(() => {
    const map = new Map<string, CampaignContentBlock[]>();
    for (const b of allBlocks ?? []) {
      const list = map.get(b.category) ?? [];
      list.push(b);
      map.set(b.category, list);
    }
    return map;
  }, [allBlocks]);

  /** Options for a `contentBlockRef`/constrained-`chips` field: the authored blocks of its refCategory
   *  (value from refValueKey|key, label from refLabelKey|label), falling back to `field.options`. */
  const refOptions = React.useCallback(
    (field: PayloadFieldDef): ReferenceOption[] => {
      const rows = field.refCategory ? blocksByCategory.get(field.refCategory) ?? [] : [];
      const derived = rows
        .map((b) => {
          const p = (b.payload ?? {}) as Record<string, unknown>;
          const rawValue = field.refValueKey ? p[field.refValueKey] : b.key;
          const value = rawValue == null ? '' : String(rawValue);
          const rawLabel = field.refLabelKey ? p[field.refLabelKey] : b.label;
          const label = rawLabel == null || rawLabel === '' ? value : String(rawLabel);
          return { value, label };
        })
        .filter((o) => o.value);
      return derived.length > 0 ? derived : field.options ?? [];
    },
    [blocksByCategory],
  );

  /** Stub-create a content block in the field's refCategory; return the value the field will store. */
  const createRefBlock = React.useCallback(
    (field: PayloadFieldDef) => async (): Promise<string | null> => {
      if (!field.refCategory) return null;
      const key = `${field.refCategory}-${Date.now().toString(36)}`;
      const payloadStub: Record<string, unknown> = {};
      if (field.refValueKey) payloadStub[field.refValueKey] = key;
      try {
        await blockMutations.create.mutateAsync({
          campaign: campaignId,
          parent: null,
          category: field.refCategory,
          key,
          label: '',
          order: 0,
          hidden: false,
          payload: payloadStub,
          metadata: {},
        });
        return key;
      } catch {
        return null;
      }
    },
    [campaignId, blockMutations.create],
  );

  const scalarFields = schema.fields.filter((f) => !isDelegated(f.type));
  const signature = React.useMemo(
    () => JSON.stringify(draftFromPayload(scalarFields, payload)),
    // scalarFields is stable for a given schema; payload drives reseed
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [payload, schema],
  );

  const [draft, setDraft] = React.useState<Record<string, unknown>>(() =>
    draftFromPayload(scalarFields, payload),
  );

  // Reseed the draft when the underlying block changes or the server value is refreshed.
  React.useEffect(() => {
    setDraft(draftFromPayload(scalarFields, payload));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [block.id, signature]);

  /** Merge the current draft over the server payload and persist — unless nothing actually changed. */
  const commit = (nextDraft: Record<string, unknown>) => {
    const merged: Record<string, unknown> = { ...payload };
    for (const f of scalarFields) merged[f.key] = toPayloadValue(f, nextDraft[f.key]);
    if (JSON.stringify(merged) === JSON.stringify(payload)) return;
    onSavePayload(merged as CampaignContentBlockPayload);
  };

  const setField = (key: string, value: unknown) => setDraft((d) => ({ ...d, [key]: value }));
  // For inputs without a natural blur (select/boolean/chips), set + commit in one step.
  const setAndCommit = (key: string, value: unknown) => {
    const next = { ...draft, [key]: value };
    setDraft(next);
    commit(next);
  };

  const commonSx = { mb: 0 } as const;

  return (
    <Stack spacing={1.5} sx={{ pt: 1.5, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
      {schema.description && (
        <Typography variant="caption" color="text.secondary">
          {schema.description}
        </Typography>
      )}

      {schema.fields.map((field) => {
        if (field.type === 'bandedOptions') {
          return (
            <QuestionBlockEditor
              key="__banded"
              block={block}
              campaignId={campaignId}
              disabled={!canWrite}
              onSave={(_b, p) => onSavePayload(p)}
            />
          );
        }
        if (field.type === 'proseQuestion') {
          return (
            <ProseQuestionEditor
              key="__prose"
              block={block}
              campaignId={campaignId}
              disabled={!canWrite}
              onSave={(_b, p) => onSavePayload(p)}
            />
          );
        }

        const value = draft[field.key];

        switch (field.type) {
          case 'select':
            return (
              <TextField
                key={field.key}
                select
                label={field.label}
                size="small"
                value={typeof value === 'string' ? value : ''}
                disabled={!canWrite}
                helperText={field.helperText}
                onChange={(e) => setAndCommit(field.key, e.target.value)}
                sx={commonSx}
              >
                {(field.options ?? []).map((opt) => (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ))}
              </TextField>
            );

          case 'capabilityRefs':
            return (
              <ReferenceMultiSelect
                key={field.key}
                label={field.label}
                value={Array.isArray(value) ? (value as string[]) : []}
                options={capabilityOptions}
                disabled={!canWrite}
                helperText={field.helperText}
                onChange={(v) => setAndCommit(field.key, v)}
                onCreate={canWrite ? createCapability : undefined}
                createLabel="+ New capability"
              />
            );

          case 'contentBlockRef':
            return (
              <ReferenceSelect
                key={field.key}
                label={field.label}
                value={typeof value === 'string' ? value : ''}
                options={refOptions(field)}
                disabled={!canWrite}
                helperText={field.helperText}
                emptyLabel="None"
                onChange={(v) => setAndCommit(field.key, v)}
                onCreate={canWrite && field.refCategory ? createRefBlock(field) : undefined}
                createLabel="+ New"
                fullWidth
              />
            );

          case 'boolean':
            return (
              <FormControlLabel
                key={field.key}
                sx={commonSx}
                control={
                  <Checkbox
                    checked={value === true}
                    disabled={!canWrite}
                    onChange={(e) => setAndCommit(field.key, e.target.checked)}
                  />
                }
                label={field.label}
              />
            );

          case 'chips': {
            // Constrained to the authored refCategory blocks (or suggestions) unless `freeTags`.
            const derivedChipOptions = field.refCategory ? refOptions(field).map((o) => o.value) : [];
            const chipOptions =
              derivedChipOptions.length > 0 ? derivedChipOptions : field.suggestions ?? [];
            return (
              <Autocomplete
                key={field.key}
                multiple
                freeSolo={field.freeTags === true}
                size="small"
                options={chipOptions}
                value={Array.isArray(value) ? (value as string[]) : []}
                disabled={!canWrite}
                onChange={(_e, v) => setAndCommit(field.key, v as string[])}
                renderTags={(tags, getTagProps) =>
                  tags.map((tag, index) => (
                    <Chip size="small" label={tag} {...getTagProps({ index })} key={tag + index} />
                  ))
                }
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label={field.label}
                    helperText={field.helperText}
                    placeholder={field.placeholder}
                  />
                )}
                sx={commonSx}
              />
            );
          }

          case 'number':
            return (
              <TextField
                key={field.key}
                label={field.label}
                size="small"
                type="number"
                value={value === '' || value == null ? '' : (value as number)}
                disabled={!canWrite}
                helperText={field.helperText}
                placeholder={field.placeholder}
                onChange={(e) => setField(field.key, e.target.value === '' ? '' : Number(e.target.value))}
                onBlur={() => commit(draft)}
                sx={commonSx}
              />
            );

          case 'lines':
            return (
              <TextField
                key={field.key}
                label={field.label}
                size="small"
                fullWidth
                multiline
                minRows={2}
                value={typeof value === 'string' ? value : ''}
                disabled={!canWrite}
                helperText={field.helperText ?? 'One per line'}
                placeholder={field.placeholder}
                onChange={(e) => setField(field.key, e.target.value)}
                onBlur={() => commit(draft)}
                sx={commonSx}
              />
            );

          case 'textarea':
          case 'html':
            return (
              // Substitutable text: the token picker inserts `{{group.key}}` references at the caret.
              // No editing-step order is available in this generic payload editor, so the picker is
              // unrestricted (all captured references listed); the resolver still degrades verbatim.
              <TokenTextField
                key={field.key}
                label={field.label}
                value={typeof value === 'string' ? value : ''}
                disabled={!canWrite}
                campaignId={campaignId}
                minRows={2}
                helperText={field.helperText}
                placeholder={field.placeholder}
                onChange={(next) => setField(field.key, next)}
                onBlur={() => commit(draft)}
                sx={commonSx}
              />
            );

          case 'text':
          default: {
            const isEmpty = !(typeof value === 'string' && value.trim());
            const showWarning = field.warnIfEmpty === true && isEmpty;
            return (
              <TextField
                key={field.key}
                label={field.label}
                size="small"
                fullWidth
                value={typeof value === 'string' ? value : ''}
                disabled={!canWrite}
                error={showWarning}
                helperText={
                  showWarning
                    ? `${field.helperText ? `${field.helperText} — ` : ''}Required: an empty id breaks the link this field provides.`
                    : field.helperText
                }
                placeholder={field.placeholder}
                onChange={(e) => setField(field.key, e.target.value)}
                onBlur={() => commit(draft)}
                sx={commonSx}
              />
            );
          }
        }
      })}
    </Stack>
  );
}

export default SchemaPayloadFields;
